/**
 * Imagem arrastada para o chat sem vir como arquivo (BRAZ-22): a foto de alguém na lista de
 * participantes, um token, uma imagem de outro site. O navegador entrega só o endereço
 * (`text/uri-list`) e o HTML da imagem (`text/html`), e a caixa de texto colaria o endereço. O chat
 * baixa a imagem e a envia como arquivo, como o clipe (ver ChatModule).
 */
import { isServerOrigin, serverUrl } from '../serverUrl.js';

/** O que o chat lê do arraste (um `DataTransfer`). */
export type DragData = { types: readonly string[]; getData(type: string): string };

export type DroppedImage = { url: string; name: string };

const IMG_SRC = /<img\b[^>]*?\ssrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;

const decodeEntities = (s: string) =>
  s
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');

/** Primeiro endereço da lista (`text/uri-list` tem um por linha; `#` é comentário). */
function firstUri(list: string): string {
  return (
    list
      .split(/\r?\n/)
      .map((l) => l.trim())
      .find((l) => l && !l.startsWith('#')) ?? ''
  );
}

/** Endereços que dá para baixar e mostrar: http(s), e os locais da própria página (data:, blob:). */
const usable = (url: string) => /^(https?:|data:image\/|blob:)/i.test(url);

/**
 * A imagem do arraste, se houver: o `<img src>` do HTML, ou o endereço quando é a foto de alguém
 * (`avatarType` traz o nome dela). Um link comum (sem imagem) não conta: continua indo para o texto.
 */
export function droppedImage(data: DragData, avatarType: string): DroppedImage | null {
  const types = [...data.types];
  const person = types.includes(avatarType) ? data.getData(avatarType).trim() : '';
  const html = types.includes('text/html') ? data.getData('text/html') : '';
  const m = IMG_SRC.exec(html);
  // o HTML de uma imagem arrastada é só a imagem; um trecho de texto selecionado com uma imagem no
  // meio é texto, e continua indo para a caixa de texto
  const onlyImage = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === '';
  const src = m && onlyImage ? decodeEntities((m[1] ?? m[2] ?? m[3] ?? '').trim()) : '';
  const url = src || (person && types.includes('text/uri-list') ? firstUri(data.getData('text/uri-list')) : '');
  if (!url || !usable(url)) return null;
  return { url, name: person || nameFromUrl(url) };
}

/** O fim do caminho, sem extensão, para nomear o arquivo ("mapa.png" → "mapa"). */
export function nameFromUrl(url: string): string {
  if (!/^https?:/i.test(url)) return '';
  try {
    const last = decodeURIComponent(new URL(url).pathname.split('/').filter(Boolean).at(-1) ?? '');
    return last.replace(/\.[a-z0-9]{2,5}$/i, '').slice(0, 60);
  } catch {
    return '';
  }
}

const EXTENSIONS: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
  'image/avif': 'avif',
  'image/bmp': 'bmp',
};

/** Nome do arquivo enviado: o nome (ou `fallback`) com a extensão do tipo da imagem. */
export function imageFileName(name: string, mime: string, fallback: string): string {
  const base = name.replace(/[\\/:*?"<>|]+/g, ' ').trim() || fallback;
  return `${base}.${EXTENSIONS[mime] ?? (mime.split('/')[1]?.replace(/[^a-z0-9]/gi, '') || 'png')}`;
}

/**
 * O chat deve mostrar "solte para enviar" (e aceitar a soltura) com este arraste? Arquivos, a foto
 * de alguém e imagens da página (o Chrome manda endereço e HTML sem texto puro; um link manda os
 * três). A decisão de verdade é na soltura (`droppedImage`): isto só acende a camada.
 */
export function looksLikeImageDrag(types: readonly string[], avatarType: string): boolean {
  return types.includes('Files') || types.includes(avatarType) || (types.includes('text/uri-list') && types.includes('text/html') && !types.includes('text/plain'));
}

/**
 * Baixa a imagem arrastada, como a grade do mapa faz (scene/imageGrid.ts):
 * - do próprio RPG Desk (a foto de alguém, um arquivo): sem cache. O endereço redireciona para o
 *   bucket, que libera CORS, mas a cópia que o `<img>` guardou veio sem esses cabeçalhos, e o
 *   navegador recusaria reaproveitá-la;
 * - de outro site: pela cópia que o servidor busca (`/api/image-fetch`, só endereço público), porque
 *   a maioria dos sites não libera CORS.
 * Devolve null se não vier uma imagem.
 */
export async function fetchDroppedImage(url: string, token: string | null): Promise<Blob | null> {
  let response: Response;
  if (/^(data|blob):/i.test(url)) response = await fetch(url);
  else {
    const target = new URL(serverUrl(url), window.location.href);
    if (isServerOrigin(target) || target.origin === window.location.origin) response = await fetch(target.href, { cache: 'no-store' });
    else if (token) response = await fetch(serverUrl(`/api/image-fetch?url=${encodeURIComponent(target.href)}`), { headers: { authorization: `Bearer ${token}` } });
    else return null;
  }
  if (!response.ok) return null;
  const blob = await response.blob();
  return blob.type.startsWith('image/') ? blob : null;
}
